import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import LogoutConfirmModal from '../../Components/Layout/LogoutConfirmModal';
import DeleteAccountConfirmModal from '../../Components/Layout/DeleteAccountConfirmModal';
import { clearLoginPrefill, saveLoginPrefill } from '../../utils/loginPrefill';
import { resolveAdminRole } from '../../utils/adminRole';
import { fetchJsonWithBackendFallback, fetchWithBackendFallback } from '../../utils/backendApi';
import '../../assets/fontello-change/fontello-79be5077/css/fontello.css';
import logo from '../../assets/finalclipstonedesginlogo-removebg-preview.png';

const Account = () => {
  const navigate = useNavigate();

  const [user, setUser]             = useState(null);
  const [profile, setProfile]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutModalStep, setLogoutModalStep] = useState(null);

  // ── Admin flag (delete-account option is hidden for admins) ────────────────
  const [isAdmin, setIsAdmin] = useState(false);

  // ── Receipts state ───────────────────────────────────────────────────────
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptsError, setReceiptsError] = useState('');
  const [printingReceipt, setPrintingReceipt] = useState(null);
  const [receiptsVisible, setReceiptsVisible] = useState(true);

  // ── Delete account state ────────────────────────────────────────────────
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // ── 2FA State ──────────────────────────────────────────────────────────────
  const [mfaEnabled, setMfaEnabled]   = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [qrCodeSvg, setQrCodeSvg]     = useState('');
  const [factorId, setFactorId]       = useState('');
  const [authCode, setAuthCode]       = useState('');
  const [mfaError, setMfaError]       = useState('');
  const [mfaLoading, setMfaLoading]   = useState(false);

  // ── Load session + profile ─────────────────────────────────────────────────
  useEffect(() => {
    const loadAccount = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session) {
          navigate('/login');
          return;
        }

        setUser(session.user);

        // Fetch profile
        const { data: profileData, error: profileError } = await supabase
          .from('tbl_users')
          .select('*')
          .eq('email', session.user.email)
          .single();

        if (profileError) {
          console.error('Profile fetch error:', profileError);
          setError('Could not load your profile details.');
        } else {
          setProfile(profileData);
        }

        // Check real MFA status from Supabase auth (not tbl_users)
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const verified = factors?.totp?.some(f => f.status === 'verified') || false;
        setMfaEnabled(verified);

        // Determine admin role so the delete-account option can be hidden for admins. ("account-deletion")
        try {
          const { isAdmin: adminFlag } = await resolveAdminRole(session.user.email);
          setIsAdmin(Boolean(adminFlag));
        } catch (roleErr) {
          console.error('Admin role check error:', roleErr);
        }

        // Load any previously saved social club booking receipts for this member. ("receipts")
        setReceiptsLoading(true);
        try {
          const myReceipts = await fetchJsonWithBackendFallback('/receipts/mine', { requireAuth: true });
          setReceipts(Array.isArray(myReceipts) ? myReceipts : []);
        } catch (receiptErr) {
          console.error('Receipts fetch error:', receiptErr);
          setReceiptsError('Could not load your saved receipts right now.');
        } finally {
          setReceiptsLoading(false);
        }

      } catch (err) {
        console.error('Account load error:', err);
        setError('An unexpected error occurred loading your account.');
      } finally {
        setLoading(false);
      }
    };

    loadAccount();
  }, [navigate]);

  // ── Start 2FA enrolment ────────────────────────────────────────────────────
  // Clears any stale unverified TOTP factors, then calls Supabase mfa.enroll()
  // to generate a new QR code the user scans with their authenticator app. ("2fa")
  const handleStart2FA = async () => {
    setMfaError('');
    setMfaLoading(true);
    try {
      // Remove any existing unverified factors first
      const { data: existing } = await supabase.auth.mfa.listFactors();
      const unverified = existing?.totp?.filter(f => f.status === 'unverified') || [];
      for (const factor of unverified) {
        await supabase.auth.mfa.unenroll({ factorId: factor.id });
      }

      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: `Clipstone Account (${user?.email})`
      });

      if (enrollError) throw enrollError;

      setFactorId(data.id);
      setQrCodeSvg(data.totp.qr_code);
      setIsEnrolling(true);
    } catch (err) {
      console.error('2FA Setup Error:', err);
      setMfaError(err.message || 'Failed to start 2FA setup.');
    } finally {
      setMfaLoading(false);
    }
  };

  // ── Verify 2FA code ────────────────────────────────────────────────────────
  // Submits the 6-digit TOTP code to Supabase challengeAndVerify to complete enrolment,
  // then mirrors the MFA flag into tbl_users so the app knows 2FA is active. ("2fa")
  const handleVerify2FA = async () => {
    setMfaError('');
    setMfaLoading(true);
    try {
      const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: authCode
      });

      if (verifyError) throw verifyError;

      // Sync MFA flag to tbl_users
      const { error: dbError } = await supabase
        .from('tbl_users')
        .update({ MFA: true })
        .eq('email', user.email);

      if (dbError) console.error('tbl_users MFA sync error:', dbError);

      setMfaEnabled(true);
      setIsEnrolling(false);
      setQrCodeSvg('');
      setAuthCode('');
    } catch (err) {
      console.error('2FA Verification Error:', err);
      setMfaError(err.message || 'Invalid code. Please try again.');
    } finally {
      setMfaLoading(false);
    }
  };

  // ── Cancel 2FA setup ───────────────────────────────────────────────────────
  // Unenrolls the pending (unverified) factor so it doesn't remain as a dangling
  // incomplete factor in the user's Supabase auth record. ("2fa")
  const handleCancel2FA = async () => {
    // Clean up the unverified factor if user cancels
    if (factorId) {
      await supabase.auth.mfa.unenroll({ factorId }).catch(console.error);
    }
    setIsEnrolling(false);
    setQrCodeSvg('');
    setAuthCode('');
    setMfaError('');
    setFactorId('');
  };

  // ── Sign out ───────────────────────────────────────────────────────────────
  // Calls Supabase signOut, clears all localStorage tokens and admin flags,
  // then redirects to the login page. ("auth")
  const performSignOut = async (shouldSaveDetails) => {
    setLoggingOut(true);

    if (shouldSaveDetails) {
      saveLoginPrefill({
        email: user?.email,
        username: profile?.username || user?.user_metadata?.username || '',
      });
    } else {
      clearLoginPrefill();
    }

    try {
      await supabase.auth.signOut();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('isAdmin');
      setLogoutModalStep(null);
      navigate('/login');
    } catch (err) {
      console.error('Sign out error:', err);
      setLoggingOut(false);
    }
  };

  const handleSignOut = () => {
    setLogoutModalStep('confirm');
  };

  // ── Delete account ─────────────────────────────────────────────────────────
  // Permanently deletes the member's account (profile, bookings, receipts, and the
  // Supabase auth identity where possible). Never offered to admin accounts. ("account-deletion")
  const handleDeleteAccount = async () => {
    setDeleteError('');
    setDeleting(true);
    try {
      await fetchWithBackendFallback('/users/me', {
        method: 'DELETE',
        requireAuth: true,
      });

      await supabase.auth.signOut().catch(() => {});
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('isAdmin');
      clearLoginPrefill();
      setDeleteModalOpen(false);
      navigate('/login');
    } catch (err) {
      console.error('Delete account error:', err);
      setDeleteError(err.message || 'Unable to delete your account right now. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Currency formatter for receipts ─────────────────────────────────────────
  const receiptCurrency = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 2,
  });

  const handlePrintReceipt = (receiptItem) => {
    setPrintingReceipt(receiptItem);
    // Wait for the printable panel to render before invoking the browser print dialog.
    setTimeout(() => window.print(), 50);
  };

  // ── Format date ────────────────────────────────────────────────────────────
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = typeof timestamp === 'number'
      ? new Date(timestamp * 1000)
      : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logoRow}>
            <img src={logo} alt="Clipstone Miners Welfare Logo" style={styles.logoImage} />
          </div>
          <div style={styles.loadingBox}>
            <div style={styles.spinner} />
            <p style={styles.loadingText}>Loading your account...</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="auth-page-container" style={styles.container}>
      <div className="auth-card" style={styles.card}>

        <div style={styles.logoRow}>
          <img src={logo} alt="Clipstone Miners Welfare Logo" style={styles.logoImage} />
        </div>

        <h2 style={styles.title}>My Account</h2>

        {error && <div style={styles.errorAlert}>{error}</div>}

        {/* Avatar */}
        <div id="account-avatar-section" style={styles.avatarSection}>
          <div style={styles.avatar}>
            {(profile?.username || user?.user_metadata?.username)
              ? (profile?.username || user?.user_metadata?.username).charAt(0).toUpperCase()
              : user?.email?.charAt(0).toUpperCase()}
          </div>
          <div>
            <p style={styles.avatarName}>{profile?.username || user?.user_metadata?.username || 'Member'}</p>
            <p style={styles.avatarEmail}>{user?.email}</p>
          </div>
        </div>

        <div style={styles.divider} />

        {/* Details */}
        <div style={styles.detailsGrid}>

          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Username</span>
            <span style={styles.detailValue}>{profile?.username || user?.user_metadata?.username || '—'}</span>
          </div>

          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Email</span>
            <span style={styles.detailValue}>{user?.email || '—'}</span>
          </div>

          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Email Verified</span>
            <span style={styles.detailValue}>
              {user?.email_confirmed_at
                ? <span style={styles.badgeGreen}><i className="icon-calendar" style={styles.badgeIcon} /> Verified</span>
                : <span style={styles.badgeRed}><i className="icon-left" style={styles.badgeIcon} /> Not verified</span>}
            </span>
          </div>

          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Member Since</span>
            <span style={styles.detailValue}>
              {formatDate(profile?.['created at'] || user?.created_at)}
            </span>
          </div>

          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Last Sign In</span>
            <span style={styles.detailValue}>
              {formatDate(user?.last_sign_in_at)}
            </span>
          </div>

          {/* 2FA row */}
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Two-Factor Auth</span>
            <span style={styles.detailValue}>
              {mfaEnabled ? (
                <span style={styles.badgeGreen}><i className="icon-calendar" style={styles.badgeIcon} /> Enabled</span>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                  {!isEnrolling && (
                    <button
                      style={styles.setupMfaButton}
                      onClick={handleStart2FA}
                      disabled={mfaLoading}
                    >
                      {mfaLoading ? 'Loading...' : 'Set Up 2FA'}
                    </button>
                  )}
                  {mfaError && !isEnrolling && (
                    <p style={{ color: '#dc2626', fontSize: '12px', margin: 0 }}><i className="icon-left-circled" style={{ marginRight: '6px' }} /> {mfaError}</p>
                  )}
                </div>
              )}
            </span>
          </div>

        </div>

        {/* 2FA Panel */}
        {isEnrolling && (
          <div style={styles.mfaPanel}>
            <h3 style={styles.mfaPanelTitle}>Configure Authenticator App</h3>
            <p style={styles.mfaPanelText}>
              Scan this QR code with an authenticator app (Google Authenticator, Microsoft Authenticator, Duo, etc.).
            </p>

            {qrCodeSvg && (
              <div
                style={styles.qrContainer}
                dangerouslySetInnerHTML={{ __html: qrCodeSvg }}
              />
            )}

            {mfaError && <div style={styles.errorAlert}>{mfaError}</div>}

            <div style={styles.mfaInputGroup}>
              <input
                type="text"
                placeholder="Enter 6-digit code"
                maxLength={6}
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value.replace(/\D/g, ''))}
                style={styles.mfaInput}
              />
              <div style={styles.mfaActionRow}>
                <button
                  style={styles.verifyMfaButton}
                  onClick={handleVerify2FA}
                  disabled={mfaLoading || authCode.length !== 6}
                >
                  {mfaLoading ? 'Verifying...' : 'Verify & Activate'}
                </button>
                <button
                  style={styles.cancelMfaButton}
                  onClick={handleCancel2FA}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={styles.divider} />

        {/* My Receipts */}
        <div id="account-receipts-section">
          <div style={styles.receiptsSectionHeader}>
            <h3 style={{ ...styles.sectionTitle, margin: 0 }}>My Receipts</h3>
            <button
              style={styles.toggleReceiptsButton}
              onClick={() => setReceiptsVisible((v) => !v)}
              aria-expanded={receiptsVisible}
            >
              {receiptsVisible ? 'Hide' : 'Show'}
            </button>
          </div>
          {receiptsVisible && (
            <>
              {receiptsLoading && <p style={styles.mfaPanelText}>Loading your receipts...</p>}
              {receiptsError && <div style={styles.errorAlert}>{receiptsError}</div>}
              {!receiptsLoading && !receiptsError && receipts.length === 0 && (
                <p style={styles.mfaPanelText}>You don't have any saved receipts yet. Receipts from Social Club room bookings you make while signed in will appear here.</p>
              )}
              {receipts.length > 0 && (
                <div style={styles.receiptList}>
                  {receipts.map((r) => (
                    <div key={r.receipt_number} style={styles.receiptRow}>
                      <div>
                        <p style={styles.receiptTitle}>{r.suite_name || 'Room booking'} — {r.receipt_number}</p>
                        <p style={styles.receiptMeta}>
                          Booked {r.preferred_date || 'n/a'} · Saved {formatDate(r.created_at)} · {receiptCurrency.format(r.costs?.total || 0)}
                        </p>
                      </div>
                      <button style={styles.printReceiptButton} onClick={() => handlePrintReceipt(r)}>
                        Print
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div style={styles.divider} />

        {/* Actions */}
        <div style={styles.actions}>
          <button id="account-back-home" style={styles.homeButton} onClick={() => navigate('/')}>
            <i className="icon-left" style={styles.actionIcon} /> Back to Home
          </button>
          <button
            id="account-sign-out"
            style={loggingOut
              ? { ...styles.signOutButton, ...styles.buttonDisabled }
              : styles.signOutButton}
            onClick={handleSignOut}
            disabled={loggingOut}
          >
            {loggingOut ? 'Signing out...' : 'Sign Out'}
          </button>
        </div>

        {/* Danger Zone — delete account is never offered to admins */}
        {!isAdmin && (
          <>
            <div style={styles.divider} />
            <div id="account-danger-zone" style={styles.dangerZone}>
              <h3 style={styles.dangerZoneTitle}>Danger Zone</h3>
              <p style={styles.mfaPanelText}>
                Deleting your account permanently erases your profile, bookings, and saved receipts. This cannot be undone.
              </p>
              {deleteError && <div style={styles.errorAlert}>{deleteError}</div>}
              <button
                id="account-delete-account"
                style={styles.deleteAccountButton}
                onClick={() => setDeleteModalOpen(true)}
              >
                Delete Account
              </button>
            </div>
          </>
        )}

      </div>

      {/* Printable receipt panel — only visible when printing via window.print() */}
      {printingReceipt && (
        <div className="account-print-only" style={styles.printReceiptPanel}>
          <h2>Receipt</h2>
          <p><strong>Receipt number:</strong> {printingReceipt.receipt_number}</p>
          <p><strong>Saved:</strong> {formatDate(printingReceipt.created_at)}</p>
          <p><strong>Customer:</strong> {printingReceipt.customer?.fullName}</p>
          <p><strong>Email:</strong> {printingReceipt.customer?.email}</p>
          <p><strong>Phone:</strong> {printingReceipt.customer?.phone}</p>
          <p><strong>Suite:</strong> {printingReceipt.suite_name}</p>
          <p><strong>Preferred date:</strong> {printingReceipt.preferred_date}</p>
          <p><strong>Payment:</strong> {printingReceipt.payment?.method} ({printingReceipt.payment?.reference})</p>
          <p><strong>Total:</strong> {receiptCurrency.format(printingReceipt.costs?.total || 0)}</p>
        </div>
      )}

      <LogoutConfirmModal
        isOpen={Boolean(logoutModalStep)}
        step={logoutModalStep}
        userEmail={user?.email || ''}
        isSubmitting={loggingOut}
        onCancel={() => {
          if (!loggingOut) setLogoutModalStep(null);
        }}
        onContinue={() => setLogoutModalStep('save-details')}
        onSaveAndLogout={() => performSignOut(true)}
        onLogoutWithoutSaving={() => performSignOut(false)}
      />

      <DeleteAccountConfirmModal
        isOpen={deleteModalOpen}
        userEmail={user?.email || ''}
        isSubmitting={deleting}
        error={deleteError}
        onCancel={() => {
          if (!deleting) {
            setDeleteModalOpen(false);
            setDeleteError('');
          }
        }}
        onConfirm={handleDeleteAccount}
      />
    </div>
  );
};

// ─── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    padding: '40px 20px',
    background: 'linear-gradient(135deg, #f9f8f6 0%, #e8e6e1 100%)',
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
  card: {
    background: '#ffffff',
    padding: 'clamp(20px, 5vw, 50px)',
    borderRadius: '16px',
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '520px',
    border: '1px solid #e5e5e5',
  },
  logoRow: { display: 'flex', justifyContent: 'center', marginBottom: '24px' },
  logoImage: { height: 'clamp(64px, 15vw, 100px)', width: 'auto' },
  title: { margin: '0 0 32px 0', textAlign: 'center', color: '#1e3f32', fontSize: '24px', fontWeight: '700' },
  loadingBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '32px 0' },
  spinner: { width: '44px', height: '44px', border: '4px solid #e5e7eb', borderTop: '4px solid #1e3f32', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  loadingText: { color: '#6b7280', fontSize: '15px', margin: 0 },
  errorAlert: { backgroundColor: '#fff0f0', color: '#dc2626', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '20px', border: '1px solid #fca5a5' },
  avatarSection: { display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px', flexWrap: 'wrap' },
  avatar: { width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#1e3f32', color: '#ffffff', fontSize: '26px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  avatarName: { margin: '0 0 4px 0', fontSize: '18px', fontWeight: '700', color: '#1e3f32' },
  avatarEmail: { margin: 0, fontSize: '14px', color: '#6b7280' },
  divider: { borderTop: '1px solid #e5e7eb', margin: '24px 0' },
  detailsGrid: { display: 'flex', flexDirection: 'column', gap: '16px' },
  detailRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #f3f4f6', flexWrap: 'wrap', rowGap: '8px' },
  detailLabel: { fontSize: '14px', color: '#6b7280', fontWeight: '500' },
  detailValue: { fontSize: '14px', color: '#111827', fontWeight: '600', textAlign: 'right', maxWidth: '100%', wordBreak: 'break-word' },
  badgeGreen: { backgroundColor: '#ecfdf5', color: '#059669', padding: '3px 10px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', border: '1px solid #6ee7b7' },
  badgeRed: { backgroundColor: '#fff0f0', color: '#dc2626', padding: '3px 10px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', border: '1px solid #fca5a5' },
  setupMfaButton: { backgroundColor: '#1e3f32', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  mfaPanel: { marginTop: '24px', padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' },
  mfaPanelTitle: { margin: '0 0 8px 0', fontSize: '16px', color: '#1e3f32', fontWeight: '700' },
  mfaPanelText: { margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280', lineHeight: '1.5' },
  qrContainer: { display: 'flex', justifyContent: 'center', marginBottom: '16px', padding: '10px', backgroundColor: '#fff', border: '1px solid #e5e7eb', width: 'fit-content', margin: '0 auto 16px auto', borderRadius: '8px' },
  mfaInputGroup: { display: 'flex', flexDirection: 'column', gap: '12px' },
  mfaInput: { padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', textAlign: 'center', letterSpacing: '4px', fontSize: '18px', outline: 'none' },
  mfaActionRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  verifyMfaButton: { flex: 1, backgroundColor: '#059669', color: '#fff', border: 'none', padding: '12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' },
  cancelMfaButton: { flex: 1, backgroundColor: 'transparent', color: '#1e3f32', border: '1px solid #d1d5db', padding: '12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' },
  actions: { display: 'flex', gap: '12px', justifyContent: 'space-between', flexWrap: 'wrap' },
  homeButton: { flex: 1, padding: '14px', backgroundColor: 'transparent', color: '#1e3f32', border: '2px solid #1e3f32', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  signOutButton: { flex: 1, padding: '14px', backgroundColor: '#1e3f32', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  buttonDisabled: { backgroundColor: '#6b7280', cursor: 'not-allowed' },
  sectionTitle: { margin: '0 0 12px 0', fontSize: '16px', color: '#1e3f32', fontWeight: '700' },
  receiptsSectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  toggleReceiptsButton: { background: 'transparent', border: '1px solid #d1d5db', color: '#6b7280', borderRadius: '6px', padding: '4px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  receiptList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  receiptRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #f3f4f6', flexWrap: 'wrap' },
  receiptTitle: { margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600', color: '#111827' },
  receiptMeta: { margin: 0, fontSize: '12px', color: '#6b7280' },
  printReceiptButton: { backgroundColor: '#1e3f32', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  dangerZone: { padding: '20px', border: '1px solid #fca5a5', borderRadius: '8px', backgroundColor: '#fff7f7', marginTop: '24px' },
  dangerZoneTitle: { margin: '0 0 8px 0', fontSize: '16px', color: '#b91c1c', fontWeight: '700' },
  deleteAccountButton: { backgroundColor: '#b91c1c', color: '#fff', border: 'none', padding: '12px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  printReceiptPanel: { display: 'none' },
};

const styleTag = document.createElement('style');
styleTag.textContent = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @media print {
    body * { visibility: hidden; }
    .account-print-only, .account-print-only * { visibility: visible; }
    .account-print-only {
      display: block !important;
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      padding: 24px;
    }
  }
`;
document.head.appendChild(styleTag);

export default Account;